export const DEFAULT_MENU_THEME = {
  restaurantName: "Restaurant",
  apiBaseUrl: "http://3.140.128.111/api/v1",
  client_id: "",
  primaryColor: "#b71c1c",
  secondaryColor: "#fdecec",
  bgColor: "#fff8f8",
  surfaceColor: "#ffffff",
  textColor: "#1f2937",
  accentTextColor: "#ffffff",
  defaultServiceMode: "delivery",
  currency: "$",
};

export const SERVICE_MODES = [
  { label: "Delivery", value: "delivery" },
  { label: "Pickup", value: "pickup" },
  { label: "Dine In", value: "dine_in" },
];

const sanitizeColor = (value, fallback) => {
  if (!value) return fallback;
  const trimmed = String(value).trim();
  const isHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(trimmed);
  const isRgb = /^rgb(a?)\((.*?)\)$/.test(trimmed);
  return isHex || isRgb ? trimmed : fallback;
};

const stripTrailingSlash = (value = "") => value.replace(/\/$/, "");

export const getMenuIframeConfig = () => {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    ...DEFAULT_MENU_THEME,
    restaurantName:
      searchParams.get("restaurantName") || DEFAULT_MENU_THEME.restaurantName,
    apiBaseUrl:
      searchParams.get("apiBaseUrl") || DEFAULT_MENU_THEME.apiBaseUrl,
    client_id: searchParams.get("client_id") || "",
    primaryColor: sanitizeColor(
      searchParams.get("primaryColor"),
      DEFAULT_MENU_THEME.primaryColor
    ),
    secondaryColor: sanitizeColor(
      searchParams.get("secondaryColor"),
      DEFAULT_MENU_THEME.secondaryColor
    ),
    bgColor: sanitizeColor(searchParams.get("bgColor"), DEFAULT_MENU_THEME.bgColor),
    surfaceColor: sanitizeColor(
      searchParams.get("surfaceColor"),
      DEFAULT_MENU_THEME.surfaceColor
    ),
    textColor: sanitizeColor(
      searchParams.get("textColor"),
      DEFAULT_MENU_THEME.textColor
    ),
    accentTextColor: sanitizeColor(
      searchParams.get("accentTextColor"),
      DEFAULT_MENU_THEME.accentTextColor
    ),
    defaultServiceMode:
      searchParams.get("defaultServiceMode") ||
      DEFAULT_MENU_THEME.defaultServiceMode,
    currency: searchParams.get("currency") || DEFAULT_MENU_THEME.currency,
    selectedLocationId: searchParams.get("selectedLocationId") || "",
    itemId: searchParams.get("itemId") || "",
  };
};

export const sortByDisplayOrder = (list = []) =>
  [...(list || [])].sort(
    (a, b) =>
      (a?.display_order ?? Number.MAX_SAFE_INTEGER) -
      (b?.display_order ?? Number.MAX_SAFE_INTEGER)
  );

export const resolveDefaultLocation = (locations = [], selectedLocationId = "") =>
  locations.find((loc) => loc.id === selectedLocationId) || locations[0] || null;

export const fetchJson = async (url) => {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.detail || "Unable to fetch data.");
  }

  return data?.data ?? data;
};

export const fetchClientLocations = async ({ apiBaseUrl, client_id }) => {
  const response = await fetchJson(
    `${stripTrailingSlash(apiBaseUrl)}/clients/${client_id}/locations/`
  );

  const list = Array.isArray(response) ? response : response?.results || [];
  return list.filter((location) => location?.is_active !== false);
};

export const fetchLocationMenuTree = async ({
  apiBaseUrl,
  client_id,
  locationId,
}) =>
  fetchJson(
    `${stripTrailingSlash(apiBaseUrl)}/menu/${client_id}/locations/${locationId}/tree/`
  );

export const resolvePriceValue = (pricing, serviceMode = "delivery") => {
  if (!pricing || typeof pricing !== "object") return null;

  const value =
    pricing[serviceMode] ?? pricing.delivery ?? pricing.pickup ?? pricing.dine_in;

  return value == null ? null : Number(value);
};

export const getDefaultSelections = (item = {}) => {
  const selections = {};

  sortByDisplayOrder(item?.variant_groups).forEach((group) => {
    const variant = sortByDisplayOrder(group?.variants).find(
      (row) => row?.is_available !== false
    );

    if (variant) {
      selections[group.id] = variant;
    }
  });

  return selections;
};

export const resolveItemPrice = (
  item = {},
  serviceMode = "delivery",
  selections = {}
) => {
  let basePrice = resolvePriceValue(item?.pricing, serviceMode);
  let selectedVariant = null;

  if (basePrice == null) {
    const groups = sortByDisplayOrder(item?.variant_groups);

    for (const group of groups) {
      const selected =
        selections[group.id] ||
        sortByDisplayOrder(group?.variants).find(
          (row) => row?.is_available !== false
        );

      const price = resolvePriceValue(selected?.pricing, serviceMode);

      if (price != null) {
        basePrice = price;
        selectedVariant = selected;
        break;
      }
    }
  }

  if (basePrice == null) {
    const directVariant = sortByDisplayOrder(item?.direct_variants).find(
      (row) => row?.is_available !== false
    );

    const directPrice = resolvePriceValue(directVariant?.pricing, serviceMode);

    if (directPrice != null) {
      basePrice = directPrice;
      selectedVariant = directVariant;
    }
  }

  const discountValue = Number(item?.discount_value || 0);
  let finalPrice = basePrice;
  let originalPrice = null;

  if (
    basePrice != null &&
    item?.discount_type === "flat" &&
    discountValue > 0
  ) {
    originalPrice = basePrice;
    finalPrice = Math.max(0, basePrice - discountValue);
  }

  return {
    basePrice,
    finalPrice,
    originalPrice,
    selectedVariant,
  };
};

export const formatPrice = (value, currency = "$") => {
  if (value == null || Number.isNaN(Number(value))) {
    return "Select options";
  }

  return `${currency}${Number(value).toFixed(2)}`;
};

export const findItemInMenu = (menuSections = [], itemId = "") => {
  for (const section of sortByDisplayOrder(menuSections)) {
    for (const category of sortByDisplayOrder(section?.categories)) {
      for (const item of sortByDisplayOrder(category?.items)) {
        if (item?.id === itemId) {
          return { section, category, item };
        }
      }
    }
  }

  return null;
};

export const buildCartStorageKey = ({ client_id, locationId }) =>
  `rmt_menu_cart_${client_id}_${locationId}`;

export const readCart = ({ client_id, locationId }) => {
  try {
    const raw = localStorage.getItem(
      buildCartStorageKey({ client_id, locationId })
    );
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

export const writeCart = ({ client_id, locationId, cart }) => {
  localStorage.setItem(
    buildCartStorageKey({ client_id, locationId }),
    JSON.stringify(cart)
  );

  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: "RMT_MENU_CART_UPDATED",
        client_id,
        locationId,
        count: getCartCount(cart),
      },
      "*"
    );
  }
};

export const buildSelectionKey = (item, selections = {}) => {
  const ids = Object.values(selections)
    .map((variant) => variant?.id)
    .filter(Boolean);

  return `${item?.id || "item"}__${ids.join("__") || "base"}`;
};

export const getCartCount = (cart = {}) =>
  Object.values(cart).reduce(
    (sum, entry) => sum + Number(entry?.quantity || 0),
    0
  );

export const getItemTotalQty = (cart = {}, itemId = "") =>
  Object.values(cart).reduce((sum, entry) => {
    return entry?.itemId === itemId
      ? sum + Number(entry?.quantity || 0)
      : sum;
  }, 0);

export const getSelectedVariantLabel = (selections = {}) =>
  Object.values(selections)
    .map((variant) => variant?.name)
    .filter(Boolean)
    .join(" • ");

export const syncIframeHeight = (source) => {
  const height = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    800
  );

  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: "IFRAME_HEIGHT_CHANGE",
        source,
        height,
      },
      "*"
    );
  }
};

export const postMenuNavigate = (path) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: "IFRAME_MENU_NAVIGATE",
        path,
      },
      "*"
    );
  }
};